/**
 * Utility functions for parsing drilling results Excel files
 */
import readXlsxFile, {type Row} from 'read-excel-file'

import type {
  DrillingPosition,
  DrillingValidationError,
  ParsedDrillingData
} from '@refra/modules/refra-planning/types'

const HEADER_KEYWORDS = ['meter']

/**
 * Read Excel file content
 */
export const readDrillingFileContent = async (file: File): Promise<Row[]> => {
  return readXlsxFile(file)
}

/**
 * Find the header row index (row starting with "Meter")
 */
export const findDrillingHeaderRowIndex = (rows: Row[]): number => {
  for (let i = 0; i < rows.length; i++) {
    const firstCell = String(rows[i]?.[0] ?? '')
      .toLowerCase()
      .trim()
    if (HEADER_KEYWORDS.includes(firstCell)) {
      return i
    }
  }
  return -1
}

/**
 * Validate drilling file data against expected drilling positions
 */
export const validateDrillingFileData = (
  rows: Row[],
  headerRowIndex: number,
  enabledPositions: DrillingPosition[]
): DrillingValidationError[] => {
  const errors: DrillingValidationError[] = []

  if (headerRowIndex === -1) {
    errors.push({
      type: 'missing_header',
      message: 'Header row not found. The file must contain a row starting with "Meter".'
    })
    return errors
  }

  const headers = rows[headerRowIndex].map((h) => String(h).trim())
  // Skip first two columns (Meter, Drilling Position) to get angle columns
  const filePositionNames = headers.slice(2)
  const expectedPositionNames = new Set(enabledPositions.map((p) => p.name.trim().toLowerCase()))

  // Check for missing positions
  const missingPositions = enabledPositions.filter(
    (p) => !filePositionNames.some((fp) => fp.toLowerCase() === p.name.trim().toLowerCase())
  )
  if (missingPositions.length > 0) {
    errors.push({
      type: 'missing_positions',
      message: 'Missing drilling positions in file. These positions will not be updated.',
      details: missingPositions.map((p) => p.name)
    })
  }

  // Check for unknown positions
  const unknownPositions = filePositionNames.filter(
    (fp) => !expectedPositionNames.has(fp.toLowerCase())
  )
  if (unknownPositions.length > 0) {
    errors.push({
      type: 'unknown_positions',
      message: 'Unknown drilling positions found in file. These columns will be ignored.',
      details: unknownPositions
    })
  }

  return errors
}

/**
 * Parse drilling data from rows
 */
export const parseDrillingFileData = (rows: Row[], headerRowIndex: number): ParsedDrillingData => {
  if (headerRowIndex === -1 || rows.length <= headerRowIndex + 1) {
    return []
  }

  const headers = rows[headerRowIndex].map((h) => String(h).trim())
  // Angle columns start from index 2 (after Meter and Drilling Position)
  const angleHeaders = headers.slice(2)

  return rows.slice(headerRowIndex + 1).reduce<ParsedDrillingData>((acc, row) => {
    // Column 0 is Meter, Column 1 is Drilling Position
    const meter = Number(row[0])
    if (isNaN(meter)) return acc

    const values = angleHeaders.map((positionName, index) => {
      const cellValue = row[index + 2] // +2 to skip Meter and Drilling Position columns
      const depth =
        cellValue !== null && cellValue !== undefined && cellValue !== '' ? Number(cellValue) : null

      return {
        positionName,
        depth: depth !== null && isNaN(depth) ? null : depth
      }
    })

    acc.push({meter, values})
    return acc
  }, [])
}
